import React, { createContext, useContext } from "react";

/**
 * Broadcasts tables are CSS grid, not `<table>` — fixed numeric columns,
 * `minmax(0,1fr)` for the name, right-aligned mono numerals. Divs with explicit
 * ARIA roles keep the grid layout while assistive tech still sees a table.
 *
 * WHY THE SCROLL CONTAINER LIVES IN HERE
 * --------------------------------------
 * The spec says tables scroll horizontally inside their own container and the
 * page body never does. Every hand-rolled table so far has broken that rule, so
 * `GridTable` owns the scroller itself: a caller cannot forget it.
 *
 * The mechanism is a pair, and the pairing is the whole point — the OUTER div
 * carries `overflow-x-auto` and no width of its own, while the INNER `role=table`
 * element carries the inline `minWidth`. Put the min-width on the scroller
 * instead and the scroller stops fitting the viewport, which pushes the page
 * body sideways: exactly the bug this component exists to prevent.
 */

/**
 * Carries the caller's `grid-template-columns` string down to heads and rows so
 * the track list is written once per table rather than once per row. Private on
 * purpose: rows are only ever meaningful inside a GridTable.
 */
const GridTemplateContext = createContext("minmax(0,1fr)");

/** Joins class fragments without template literals, which Tailwind cannot purge. */
const cx = (...parts) => parts.filter(Boolean).join(" ");

/** Header-cell typography. Kept whole so Tailwind retains the classes. */
const HEAD_TEXT =
  "text-[11px] uppercase tracking-[0.06em] text-bc-faint font-semibold";

/** Body-cell typography. */
const BODY_TEXT = "text-[13px] text-bc-text-2";

/**
 * Scrolling grid "table". Renders an outer scroller wrapping the ARIA table.
 *
 * @param {Object} props
 * @param {string} props.columns - CSS grid-template-columns, e.g. "minmax(0,1fr) 92px repeat(4,minmax(52px,78px))".
 * @param {React.ReactNode} props.children - GridHead and GridRow elements.
 * @param {string} [props.className] - Extra classes for the inner table element.
 * @param {string} [props.label] - Accessible name for the table.
 * @param {number} [props.minWidth=640] - Inline min-width on the INNER element so the OUTER div scrolls.
 */
export const GridTable = ({
  columns,
  children,
  className = "",
  label,
  minWidth = 640,
}) => (
  <div className="overflow-x-auto">
    <GridTemplateContext.Provider value={columns}>
      <div
        role="table"
        aria-label={label}
        style={{ minWidth }}
        className={cx(
          "rounded-[10px] border border-bc-border bg-white",
          className
        )}
      >
        {/* A role=table needs table > rowgroup > row > cell to be a valid tree. */}
        <div role="rowgroup">{children}</div>
      </div>
    </GridTemplateContext.Provider>
  </div>
);

/**
 * Header row. Applies the table's grid template from context.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - GridCell elements with `head`.
 * @param {string} [props.className] - Extra classes.
 */
export const GridHead = ({ children, className = "" }) => {
  const gridTemplateColumns = useContext(GridTemplateContext);

  return (
    <div
      role="row"
      style={{ gridTemplateColumns }}
      className={cx(
        "grid items-center gap-3 bg-bc-subtle border-b border-bc-border",
        HEAD_TEXT,
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * Body row. Applies the table's grid template from context.
 *
 * When `onClick` is supplied the row becomes genuinely keyboard-operable:
 * focusable, activated by Enter and Space, with a visible focus ring. Space is
 * prevent-defaulted so activating a row does not also scroll the page. Without
 * `onClick` the row stays inert — no tab stop, no pointer cursor, no hover.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - GridCell elements.
 * @param {string} [props.className] - Extra classes.
 * @param {(event: React.SyntheticEvent) => void} [props.onClick] - Activation handler.
 * @param {string} [props.label] - Accessible name, so an activatable row announces something meaningful.
 */
export const GridRow = ({ children, className = "", onClick, label }) => {
  const gridTemplateColumns = useContext(GridTemplateContext);

  const handleKeyDown = (event) => {
    if (!onClick) return;
    if (event.key === "Enter") {
      onClick(event);
      return;
    }
    if (event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <div
      role="row"
      aria-label={label}
      style={{ gridTemplateColumns }}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      className={cx(
        "grid items-center gap-3 border-b border-bc-hairline-lt last:border-b-0",
        onClick &&
          "cursor-pointer hover:bg-bc-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-inset",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * One cell. `min-w-0` is load-bearing: without it long content refuses to
 * shrink and blows out a `minmax(0,1fr)` track instead of truncating.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Cell content.
 * @param {string} [props.className] - Extra classes.
 * @param {boolean} [props.numeric=false] - Right-align with tabular mono numerals.
 * @param {boolean} [props.head=false] - Render as a column header.
 */
export const GridCell = ({
  children,
  className = "",
  numeric = false,
  head = false,
}) => (
  <div
    role={head ? "columnheader" : "cell"}
    className={cx(
      "px-4 py-3 min-w-0",
      head ? HEAD_TEXT : BODY_TEXT,
      numeric && "text-right font-mono tabular-nums",
      className
    )}
  >
    {children}
  </div>
);
